import { useEffect, useState, useCallback, useMemo } from "react";
import { Formik, ErrorMessage, Field, FieldArray } from "formik";
import {
  ContainerForm,
  ContainerInput,
  ErrorStyled,
  ContainerButton,
  InputStyled,
  ContainerCards,
  ContainerInputTel,
  InputStyledTel,
  SelectStyledTel,
  ButtonTelTrash,
  ButtonTel,
  ContainerPrice,
  ContainerTotal,
  SectionTitle,
} from "./formularioStyles";
import ButtonForm from "../buttonForm/ButtonForm";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../spinner/Spinner";
import InputPredictivo from "../inputPredictivo/InputPredictivo";
import { validationFormulario } from "../../formik/validationSchema";
import { initialValuesFormulario } from "../../formik/initialValues";
import {
  VENTAS_SUSPENDIDAS,
  MENSAJE_VENTAS_SUSPENDIDAS,
} from "../../config/mantenimiento";
import {
  setDepartamento,
  setFechNac,
  setProvincia,
  setLocalidad,
  setSucursal,
  setSegmento,
  setPuerta,
  setDpto,
  setPiso,
  setNombre,
  setApellido,
  setGenero,
  setEmail,
  setUbicacion,
  clearPlanComercial,
  setUsuarioId,
  resetFormulario,
} from "../../redux/form/formSlice";
import Tarjeta from "../tarjeta/Tarjeta";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../redux/user/userSlice";
import { formatoPrecio } from "../../utils/formatPrecio";
import Modal from "../modal/Modal";
import BackButton from "../backButton/BackButton";
import { FaTrashAlt } from "react-icons/fa";
import { apiClient } from "../../axios/apiClient";
import { createClient } from "../../axios/axiosForm";
import { useFetch } from "../../hook/useFetch";
import { useEnvioUnico } from "../../hook/useEnvioUnico";
import {
  nuevoId,
  registrarEvento,
  registrarVenta,
} from "../../registro/registro";

// Nombre de un ítem de catálogo por id (los selects guardan ids).
const nombrePorId = (lista, id) =>
  (Array.isArray(lista) ? lista : []).find((x) => String(x.id) === String(id))
    ?.nombre ?? null;

// Resultado de createClient → estado para el registro.
const resultadoDeVenta = (response, duracionMs) => {
  if (response?.status?.success === true) {
    return {
      estado: "OK",
      resultado: {
        clienteId: response?.data?.id ?? null,
        httpStatus: response?.httpStatus ?? null,
        mensaje: response.status.message ?? null,
        respuesta: { status: response.status, data: response.data },
        duracionMs,
      },
    };
  }
  return {
    estado: response?.sinRespuesta ? "INCIERTO" : "ERROR",
    resultado: {
      httpStatus: response?.httpStatus ?? null,
      mensaje: response?.message ?? null,
      respuesta: { statusCode: response?.statusCode ?? null },
      duracionMs,
    },
  };
};

// La cantidad de cuotas vive en el nombre de la política de activación
// (ej. "COSTO ACTIVACIÓN - 3 CUOTAS"). La extraemos para dejarla visible en Logs.
const cuotasDeDetalle = (detallePlan) =>
  (detallePlan || [])
    .map((d) => /(\d+)\s*CUOTAS?/i.exec(d?.politica || "")?.[0])
    .find(Boolean) || null;

// La línea del costo de activación dentro del detalle del plan (su política
// dice las cuotas, ej. "COSTO ACTIVACIÓN - 3 CUOTAS").
const lineaActivacion = (detallePlan) =>
  (detallePlan || []).find(
    (d) => /CUOTA/i.test(d?.politica || "") || /ACTIVACI[OÓ]N/i.test(d?.producto || ""),
  ) || null;

// Snapshot de lo que eligió el vendedor, para registrar en Logs JUSTO antes de
// enviar el alta a Iptel. Da trazabilidad (sobre todo de las cuotas) sin depender
// de SIGA. Nunca debe romper el envío.
const armarSnapshot = (values, resumen, planNombre, total, telefonosArr) => {
  const r = resumen || {};
  const cuotas = cuotasDeDetalle(r.detallePlan);
  const mensaje =
    `Eligió ${planNombre || "plan"}` +
    (cuotas ? ` · activación ${cuotas}` : "") +
    (total != null ? ` · total ${formatoPrecio(total)}` : "");
  const detalle = {
    cliente: {
      apellido: values.apellido,
      nombre: values.nombre,
      dni: values.documento,
      genero: values.genero || null,
      email: values.email || null,
      telefonos: telefonosArr.map((t) => `${t.ani}${t.tipo ? ` (${t.tipo})` : ""}`),
    },
    domicilio: {
      sucursal: r.sucursal,
      provincia: r.provincia,
      departamento: r.departamento,
      localidad: r.localidad,
      calle: r.calle,
      altura: r.altura,
      piso: r.piso,
      dpto: r.dpto,
      referencia: r.ubicacion,
    },
    planComercial: {
      plan: planNombre,
      cuotasActivacion: cuotas,
      total,
      opciones: (r.detallePlan || []).map((d) => ({
        producto: d.producto,
        politica: d.politica,
        promocion: d.promocion,
        precio: d.precio,
      })),
    },
  };
  return { mensaje, detalle };
};

const Formulario = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Venta nacida de una solicitud de servicio (zona recién habilitada): llega
  // por el estado de navegación desde "Mis ventas → Solicitudes". Precarga los
  // datos personales y la dirección en texto (la zona de catálogo se reelige,
  // porque recién ahora existe la sucursal/localidad en el catálogo de Iptel).
  const desdeSolicitud = location.state?.desdeSolicitud || null;
  const [solicitudOrigenId, setSolicitudOrigenId] = useState(
    desdeSolicitud?.origenId || null,
  );
  // Si se limpia el formulario, se oculta el aviso de "datos traídos de la
  // solicitud" (ya no quedan esos datos).
  const [prefillVisible, setPrefillVisible] = useState(
    Boolean(desdeSolicitud?.prefill),
  );

  // initialValues del formulario: vacío, o precargado si viene de una solicitud.
  const valoresIniciales = useMemo(() => {
    const p = desdeSolicitud?.prefill;
    if (!p) return initialValuesFormulario;
    return {
      ...initialValuesFormulario,
      nombre: p.nombre || "",
      apellido: p.apellido || "",
      documento: p.documento || "",
      genero: p.genero || "",
      email: p.email || "",
      telefonos:
        Array.isArray(p.telefonos) && p.telefonos.length
          ? p.telefonos
          : initialValuesFormulario.telefonos,
      calle: p.calle || "",
      puerta: p.puerta || "",
      ubicacion: p.ubicacion || "",
    };
  }, [desdeSolicitud]);

  const currentUser = useSelector((state) => state.user.currentUser);

  const currentForm = useSelector((state) => state.form);
  const dispatch = useDispatch();

  const [provSelec, setProvSelec] = useState();

  const [deparSelec, setDeparSelec] = useState();

  const [localSelec, setLocalSelec] = useState("");

  //const [calles, setCalles] = useState([]);

  const [error, setError] = useState(null);

  const [planesComercialesItem, setPlanesComercialesItem] = useState([]);
  const [ids, setIds] = useState([]);
  //Obtengo los datos de alternativas que tenga el plan comercial
  const [loadingCard, setLoadingCard] = useState(false);
  const [segmentoSel, setSegmentoSel] = useState("1");
  /*Segmentos y sucursales*/
  //const [segmentos, setSegmentos] = useState([]);
  //const [sucursales, setSucursales] = useState([]);
  /*Fin de segmentos y sucursales*/
  const [sucursalSel, setsucursalSel] = useState("");

  /*hooks*/
  const usuarioId = currentUser?.data?.metadata?.usuarioId;
  const { data: sucursales } = useFetch(
    usuarioId ? `/ventas/api/v1/Usuarios/${usuarioId}/sucursales` : null,
  );

  const { data: provincias } = useFetch(
    sucursalSel ? `/ventas/api/v1/sucursales/${sucursalSel}/provincias` : null,
  );

  const { data: departamentos, loading: loadingDepartamentos } = useFetch(
    sucursalSel && provSelec
      ? `/ventas/api/v1/sucursales/${sucursalSel}/${provSelec}/departamentos`
      : null,
  );

  const { data: localidades, loading: loadingLocalidades } = useFetch(
    sucursalSel && provSelec && deparSelec
      ? `/ventas/api/v1/sucursales/${sucursalSel}/${provSelec}/${deparSelec}/localidades`
      : null,
  );

  const { data: calles } = useFetch(
    provSelec && deparSelec && localSelec
      ? `/ventas/api/v1/sucursales/${provSelec}/${deparSelec}/${localSelec}/calles/all`
      : null,
  );

  const { data: planesComerciales } = useFetch(
    segmentoSel && sucursalSel
      ? `/productmanager/api/v1/planes/${segmentoSel}/${sucursalSel}`
      : null,
  );

  const token = currentUser?.data?.token || false;

  //Acá guardo el id de usuario para el formulario
  useEffect(() => {
    if (usuarioId != null) dispatch(setUsuarioId(usuarioId));
  }, [dispatch, usuarioId]);

  // Precarga desde una solicitud: piso y dpto viven en el slice (no en Formik),
  // así que se setean una vez al montar. El resto va por los initialValues.
  useEffect(() => {
    const p = desdeSolicitud?.prefill;
    if (!p) return;
    if (p.piso) dispatch(setPiso(p.piso));
    if (p.dpto) dispatch(setDpto(p.dpto));
    // Solo al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    const handleExpiration = () => {
      registrarEvento("web.sesion-vencida", "Sesión vencida en el formulario");
      dispatch(logout());
      navigate("/");
    };

    let timeoutId;

    try {
      const decodedToken = jwtDecode(token);
      const expirationMs = decodedToken.exp * 1000;
      const remaining = expirationMs - Date.now();

      if (remaining <= 0) {
        handleExpiration();
      } else {
        timeoutId = setTimeout(handleExpiration, remaining);
      }
    } catch (error) {
      console.error("Error al decodificar el token:", error);
      registrarEvento("web.error", `No se pudo decodificar el token: ${error?.message || "inesperado"}`, {
        ruta: window.location.pathname,
      });
      handleExpiration();
    }

    return () => clearTimeout(timeoutId);
  }, [token, navigate, dispatch]);

  // Manejar el cambio de selección de Provincia
  const handleChangeProvincia = (event) => {
    //console.log('Provincia seleccionada:', event.target.value);
    setProvSelec(event.target.value);
    dispatch(setProvincia(parseInt(event.target.value, 10)));
    //setLoading(true);
  };

  // Manejar el cambio de selección de Departamento
  const handleChangeDepartamentos = (event) => {
    //console.log('Departamento seleccionado:', event.target.value);
    setDeparSelec(event.target.value);
    dispatch(setDepartamento(parseInt(event.target.value, 10)));
  };

  // Manejar el cambio de selección de Localidades
  const handleChangeLocalidades = (event) => {
    setLocalSelec(event.target.value);
    dispatch(setLocalidad(parseInt(event.target.value, 10)));
    //console.log(localSelec);
  };

  // Manejo estatico de segmento en Hogares 1
  const handleChangeSegmento = (e) => {
    setSegmentoSel("1");
    dispatch(setSegmento("1"));
  };

  // Manejo de cambio de sucursales
  const handleChangeSucursal = (e) => {
    setsucursalSel(e.target.value);
    dispatch(setSucursal(parseInt(e.target.value, 10)));
  };

  //const [planesComerciales, setPlanesComerciales] = useState([]);
  const [planSel, setPlanSel] = useState("");

  const handleChangePlanes = (event) => {
    setPlanSel(event.target.value);
    dispatch(clearPlanComercial());
    //dispatch(setPlan(event.target.value));
  };

  /*Axios de planes comerciales con alternativas*/
  useEffect(() => {
    if (!planSel || ids.length === 0) return;

    // Limpiamos antes de cargar para evitar renders con datos viejos
    setPlanesComercialesItem([]);
    setLoadingCard(true);

    const requests = ids.map((id) =>
      apiClient.get(
        `/productmanager/api/v1/planes/${planSel}/${id}/alternativas?orderBy=id`,
      ),
    );

    Promise.all(requests)
      .then((responses) => {
        const datosObtenidos = responses.map((response) => response.data);
        // Reset de contratos: garantiza que el slice quede igual a lo visible,
        // independientemente del estado previo o de cuántos cambios de plan hubo.
        dispatch(clearPlanComercial());
        setSubtotales({});
        setConfirmados(Array(datosObtenidos.length).fill(false));
        setPlanesComercialesItem(datosObtenidos);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoadingCard(false);
      });
  }, [planSel, ids, dispatch]);

  //const [planesComercialesAlternativas, setPlanesComercialesAlternativas] = useState([]);

  /*Planes comerciales con alternativas axios*/
  useEffect(() => {
    if (!planSel) return;
    setIds([]);
    setPlanesComercialesItem([]);
    apiClient
      .get(`/productmanager/api/v1/planes/${planSel}/items?orderBy=id`)
      .then((response) => {
        const idsExtraidos = response.data.data.map((objeto) => objeto.id);
        setIds(idsExtraidos);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [planSel]);
  /*Fin de Planes comerciales  con alternativas axios*/

  //estado del formulario
  const [submitted, setSubmitted] = useState(false);
  // Bloquea el botón Guardar mientras la venta viaja a Iptel: un doble clic
  // cargaba la venta dos veces.
  const { enviando, ejecutar: enviarUnaVez } = useEnvioUnico();
  // "ok" cuando Iptel confirmó: el botón se completa en verde un instante antes
  // de mostrar el cartel de éxito.
  const [envioOk, setEnvioOk] = useState(false);

  /*Calculo y obtencion desde card para el total del plan*/
  // Subtotales por índice de tarjeta: { 0: 100, 1: 200 }. El total se deriva.
  const [subtotales, setSubtotales] = useState({});
  const total = Object.values(subtotales).reduce((acc, p) => acc + p, 0);

  //guardamos el array de confirmados(cards)
  const [confirmados, setConfirmados] = useState([]);

  // Cada tarjeta reporta su precio (al montar y al cambiar el select).
  // Marcamos la tarjeta como confirmada automáticamente.
  const actualizarSubtotal = useCallback((index, precio) => {
    setSubtotales((prev) => ({ ...prev, [index]: precio }));
    setConfirmados((prev) => {
      if (prev[index] === true) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);
  /*Fin de calculo y obtencion desde card para el total del plan */

  //mensaje para modal
  const [mensaje, setMensaje] = useState("");
  const [clienteId, setClienteId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  // Confirmación previa al envío: muestra el costo de activación / cuotas elegidas.
  const [confirmAlta, setConfirmAlta] = useState(null);

  //Limpiar el plan comercial
  const resetPlanComercial = () => {
    setPlanSel("");
    setPlanesComercialesItem([]);
    dispatch(clearPlanComercial());
    setConfirmados([]);
    setSubtotales({});
  };

  // Reset completo de la venta EN LA APP, sin recargar la página.
  // Evita el error de deep-link en Apache al recargar /formulario y cualquier
  // carrera de rehidratación. Al poner submitted en false, el Formik se vuelve
  // a montar limpio con los initialValues vacíos.
  const resetVenta = () => {
    dispatch(resetFormulario());
    // resetFormulario también limpia el usuarioId del slice; lo restauramos.
    if (usuarioId != null) dispatch(setUsuarioId(usuarioId));
    resetPlanComercial();
    setIds([]);
    setProvSelec(undefined);
    setDeparSelec(undefined);
    setLocalSelec("");
    setsucursalSel("");
    setSegmentoSel("1");
    setError(null);
    setClienteId(null);
    setMensaje("");
    setSubmitted(false);
    setEnvioOk(false);
    // Si venía de una solicitud, ya se concretó: no re-marcar en una venta nueva.
    setSolicitudOrigenId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Datos legibles de la venta para el registro (el aviso a operaciones los
  // necesita: los ids no le sirven a quien asigna la instalación).
  // Nunca debe romper el envío: ante cualquier error devuelve null.
  const armarResumen = (values, telefonosArr) => {
    try {
      const tarjetas = planesComercialesItem.filter(
        (item) => item?.data?.length > 0,
      );
      const detallePlan = (currentForm.contratos || [])
        .map((c) => {
          const alt = tarjetas[c.id]?.data?.find(
            (d) => d?.politica?.id === c.politicaId,
          );
          if (!alt) return null;
          return {
            producto: alt.producto?.nombre ?? null,
            politica: alt.politica?.nombre ?? null,
            promocion: alt.promocion?.nombre ?? null,
            precio: alt.politica?.precio ?? null,
          };
        })
        .filter(Boolean);
      return {
        sucursal: nombrePorId(sucursales, sucursalSel),
        provincia: nombrePorId(provincias, provSelec),
        departamento: nombrePorId(departamentos, deparSelec),
        localidad: nombrePorId(localidades, localSelec),
        calle: values.calle || null,
        altura: values.puerta ? String(values.puerta) : null,
        piso: currentForm.callePiso ? String(currentForm.callePiso) : null,
        dpto: currentForm.calleDepartamento
          ? String(currentForm.calleDepartamento)
          : null,
        ubicacion: values.ubicacion || null,
        email: values.email || null,
        telefonos: telefonosArr.map((t) => ({ tipo: t.tipo, numero: t.ani })),
        plan: nombrePorId(planesComerciales, planSel),
        detallePlan,
      };
    } catch {
      return null;
    }
  };

  // Envío real del alta (lo que antes estaba en onSubmit). Se dispara recién
  // cuando el vendedor confirma el costo de activación en el modal.
  const enviarAlta = (values) =>
    enviarUnaVez(async () => {
      // Declarado afuera del try para poder referenciarlo si algo falla.
      let origenId = null;
      try {
        dispatch(setNombre(values.nombre));
        dispatch(setApellido(values.apellido));
        dispatch(setGenero(values.genero));
        dispatch(setFechNac(values.fechaNac));
        dispatch(setEmail(values.email));
        dispatch(setPuerta(values.puerta));
        dispatch(setUbicacion(values.ubicacion));

        const telefonosArr = (values.telefonos || [])
          .filter((t) => t && t.numero)
          .map((t) => ({ ani: t.numero, tipo: t.tipo }));

        const payload = {
          nombre: values.nombre,
          apellido: values.apellido,
          genero: values.genero,
          fechaNacimiento: values.fechaNac,
          dni: values.documento,
          email: values.email,
          telefono: telefonosArr[0]?.ani || "",
          telefonos: telefonosArr,
          calleAltura: values.puerta,
          ubicacion: values.ubicacion,
        };

        const datosFinales = { ...currentForm, ...payload };

        // Registro en el backend: intento antes de enviar y resultado después,
        // con el mismo id. No bloquea ni afecta la venta.
        origenId = nuevoId();
        const inicio = Date.now();
        const resumen = armarResumen(values, telefonosArr);
        const planNombre = nombrePorId(planesComerciales, planSel);

        // Snapshot en Logs de lo elegido (plan, cuotas, promoción, cliente),
        // ANTES de enviar el alta. Fire-and-forget: no afecta la venta.
        const snap = armarSnapshot(values, resumen, planNombre, total, telefonosArr);
        registrarEvento("venta.snapshot", snap.mensaje, { origenVenta: origenId, ...snap.detalle });

        const registro = {
          fechaVenta: new Date(inicio).toISOString(),
          payload: datosFinales,
          resumen,
          meta: { planId: planSel ? Number(planSel) : null, planNombre, total },
          // Si la venta nace de una solicitud, el backend la marca convertida al OK.
          ...(solicitudOrigenId ? { solicitudOrigenId } : {}),
        };
        registrarVenta(origenId, { ...registro, estado: "PENDIENTE" });

        const response = await createClient(datosFinales);
        registrarVenta(origenId, {
          ...registro,
          ...resultadoDeVenta(response, Date.now() - inicio),
        });

        if (response?.status?.success === true) {
          setMensaje(response.status.message || "Cliente cargado con exito!");
          setClienteId(response?.data?.id ?? null);
          setEnvioOk(true);
          await new Promise((resolver) => setTimeout(resolver, 900));
          setSubmitted(true);
        } else {
          setModalMessage(response.message || "Error en la respuesta del servidor");
          setModalOpen(true);
          setTimeout(() => {
            setSubmitted(false);
            setMensaje(null);
          }, 2000);
        }
      } catch (error) {
        // Excepción NUESTRA durante el alta (no la respuesta de Iptel): queda en
        // los Logs como web.error para poder investigarla. Es fire-and-forget.
        registrarEvento(
          "web.error",
          `Error al enviar el alta: ${error?.message || "inesperado"}`,
          {
            origenVenta: origenId,
            stack: error?.stack ? String(error.stack).slice(0, 1500) : null,
            ruta: window.location.pathname,
          },
        );
        setModalMessage(error.message || "Ocurrió un error inesperado");
        setModalOpen(true);
        setTimeout(() => {
          setSubmitted(false);
          setError(null);
        }, 2000);
      }
    });

  // Antes de enviar: abre el modal de confirmación con el costo de activación /
  // cuotas elegidas. El form ya está validado (Formik sólo llama acá si pasa).
  const pedirConfirmacion = (values) => {
    const telefonosArr = (values.telefonos || [])
      .filter((t) => t && t.numero)
      .map((t) => ({ ani: t.numero, tipo: t.tipo }));
    const resumen = armarResumen(values, telefonosArr);
    setConfirmAlta({
      values,
      activacion: lineaActivacion(resumen?.detallePlan),
      planNombre: nombrePorId(planesComerciales, planSel),
      total,
    });
  };

  const confirmarAlta = () => {
    const v = confirmAlta?.values;
    setConfirmAlta(null);
    if (v) enviarAlta(v);
  };

  // Ventas suspendidas: no se muestra el formulario (tapa la URL directa y el
  // "Pasar a venta" desde solicitudes). El resto de la web sigue normal.
  if (VENTAS_SUSPENDIDAS) {
    return (
      <div
        style={{
          maxWidth: 520,
          margin: "3rem auto",
          padding: "0 1rem",
          textAlign: "center",
        }}
      >
        <BackButton />
        <h2 style={{ color: "var(--violetaOscuro)", marginTop: "1.5rem" }}>
          Carga de ventas suspendida
        </h2>
        <p style={{ color: "var(--grisOscuro)", lineHeight: 1.5 }}>
          {MENSAJE_VENTAS_SUSPENDIDAS}
        </p>
      </div>
    );
  }

  return (
    <>
      {submitted ? (
        <Modal
          isOpen={true}
          onClose={resetVenta}
        >
          <h2>{mensaje}</h2>
          {clienteId != null && (
            <p>
              N° de cliente: <strong>{clienteId}</strong>
            </p>
          )}
        </Modal>
      ) : (
        <Formik
          initialValues={valoresIniciales}
          validationSchema={validationFormulario}
          onSubmit={(values, { setSubmitting }) => {
            // Sólo abre la confirmación; el envío real ocurre al confirmar.
            // Libera el submit de Formik para poder reintentar si elige "No".
            pedirConfirmacion(values);
            setSubmitting(false);
          }}
        >
          {({ values, resetForm }) => (
            <ContainerForm>
              <BackButton
                confirmMessage="¿Seguro que querés salir del formulario? Vas a perder los datos cargados."
                onBeforeNavigate={() => {
                  registrarEvento("formulario.salida", "Salió con el botón Volver");
                  resetForm();
                  dispatch(resetFormulario());
                }}
              />
              <h2>Ingrese los datos de la venta</h2>
              {desdeSolicitud?.prefill && prefillVisible && (
                <div
                  style={{
                    background: "#f3eefa",
                    border: "1px solid #e0d6f0",
                    borderRadius: "8px",
                    padding: "0.75rem 1rem",
                    margin: "0 0 1rem",
                    fontSize: "0.9rem",
                    color: "var(--grisOscuro)",
                  }}
                >
                  Datos traídos de la solicitud de{" "}
                  <strong>
                    {[desdeSolicitud.prefill.apellido, desdeSolicitud.prefill.nombre]
                      .filter(Boolean)
                      .join(", ")}
                  </strong>
                  .
                  {desdeSolicitud.zona &&
                    (desdeSolicitud.zona.localidad || desdeSolicitud.zona.provincia) && (
                      <>
                        {" "}
                        La zona pedida era{" "}
                        <strong>
                          {[desdeSolicitud.zona.localidad, desdeSolicitud.zona.provincia]
                            .filter(Boolean)
                            .join(", ")}
                        </strong>
                        .
                      </>
                    )}{" "}
                  Elegí la sucursal y seleccioná la zona en el catálogo; revisá la
                  dirección y completá el plan.
                </div>
              )}
              <SectionTitle>Configuración de venta</SectionTitle>
              <ContainerInput>
                <label>Sucursal:</label>
                <Field name="sucursal">
                  {({ field, form }) => (
                    <select
                      id="sucursal"
                      onChange={(e) => {
                        field.onChange(e);
                        handleChangeSucursal(e);
                      }}
                      value={sucursalSel}
                    >
                      <option value="">Seleccione una sucursal</option>
                      {sucursales.length ? (
                        sucursales.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))
                      ) : (
                        <option>Cargando...</option>
                      )}
                    </select>
                  )}
                </Field>
              </ContainerInput>
              <ErrorMessage
                name="sucursal"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Segmento:</label>
                <Field name="segmento">
                  {({ field, form }) => (
                    <select
                      type="number"
                      id="segmento"
                      disabled
                      onChange={(e) => {
                        field.onChange(e);
                        handleChangeSegmento(e);
                      }}
                      value={segmentoSel}
                    >
                      {/* <option value="">Seleccione un segmento</option> */}
                      <option value="1" disabled>
                        HOGARES
                      </option>
                      {/* {
                      segmentos.length ?  (segmentos.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.nombre}
                            </option>
                        ))
                      ) : (<option>Cargando...</option>)  
                    } */}
                    </select>
                  )}
                </Field>
              </ContainerInput>
              <ErrorMessage
                name="segmento"
                component={ErrorStyled}
              ></ErrorMessage>
              <SectionTitle>Datos personales</SectionTitle>
              <ContainerInput>
                <label>Nombre:</label>
                <Field
                  name="nombre"
                  type="text"
                  id="nombre"
                  as={InputStyled}
                  placeholder="Nombre"
                />
              </ContainerInput>
              <ErrorMessage
                name="nombre"
                component={ErrorStyled}
                className="error-message"
              ></ErrorMessage>
              <ContainerInput>
                <label>Apellido:</label>
                <Field
                  name="apellido"
                  type="text"
                  id="apellido"
                  as={InputStyled}
                  placeholder="Apellido"
                />
              </ContainerInput>
              <ErrorMessage
                name="apellido"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>DNI:</label>
                <Field
                  name="documento"
                  type="text"
                  id="documento"
                  as={InputStyled}
                  placeholder="Ej. 22222222"
                />
              </ContainerInput>
              <ErrorMessage
                name="documento"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Género:</label>
                <Field as="select" name="genero" id="genero" type="text">
                  <option value="">Seleccione el género</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                </Field>
              </ContainerInput>
              <ErrorMessage
                name="genero"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Fecha de Nacimiento:</label>
                <Field name="fechaNac" id="fechaNac" type="date" />
              </ContainerInput>
              <ErrorMessage
                name="fechaNac"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Email:</label>
                <Field
                  name="email"
                  type="email"
                  id="email"
                  as={InputStyled}
                  placeholder="Ej. correo@ejemplo.com"
                />
              </ContainerInput>
              <ErrorMessage name="email" component={ErrorStyled}></ErrorMessage>
              <ContainerInput>
                <label>Teléfono:</label>
                {/* <Field
                  name="tel"
                  type="tel"
                  id="tel"
                  as={InputStyled}
                  placeholder="Ej. 3512345678"
                /> */}
              </ContainerInput>
              {/* <ErrorMessage name="tel" component={ErrorStyled}></ErrorMessage> */}
              <FieldArray name="telefonos">
                {({ push, remove }) => (
                  <div>
                    {values.telefonos.map((telefono, index) => (
                      <div key={index}>
                        <ContainerInputTel>
                          <label>Tipo:</label>
                          <Field
                            as={SelectStyledTel}
                            name={`telefonos[${index}].tipo`}
                          >
                            <option value="">Seleccione</option>
                            <option value="cel">Celular</option>
                            <option value="fijo">Fijo</option>
                            <option value="fax">Fax</option>
                            <option value="otro">Otro</option>
                          </Field>

                          <label>Número:</label>
                          <Field
                            name={`telefonos[${index}].numero`}
                            type="tel"
                            as={InputStyledTel}
                            placeholder="Ej. 3512345678"
                          />

                          {index > 0 && (
                            <ButtonTelTrash onClick={() => remove(index)}>
                              <FaTrashAlt />
                            </ButtonTelTrash>
                          )}
                        </ContainerInputTel>
                        <ErrorMessage
                          name={`telefonos[${index}].tipo`}
                          component={ErrorStyled}
                        />{" "}
                        <ErrorMessage
                          name={`telefonos[${index}].numero`}
                          component={ErrorStyled}
                        />
                      </div>
                    ))}
                    <ButtonTel onClick={() => push({ tipo: "", numero: "" })}>
                      + Agregar Teléfono
                    </ButtonTel>
                  </div>
                )}
              </FieldArray>
              {/* <ContainerInput>
              <label>Tipo Doc:</label>
              <Field type="text" as="select" name="tipoDoc" id="tipoDoc" >
                <option >Seleccione un tipo de documento</option>
                <option value="dni">DNI</option>
                <option value="cuil">CUIL/CUIT</option>
              </Field> 
            </ContainerInput>
            <ErrorMessage name='tipoDoc' component={ErrorStyled}></ErrorMessage> */}
              {/* <ContainerInput>
              <label>Propiedad:</label>
              <Field type="text" as="select" name="propiedad" id="propiedad" >
                <option >Seleccione la propiedad</option>
                <option value="inquilino">Inquilino</option>
                <option value="propietario">Propietario</option>
              </Field> 
            </ContainerInput>
            <ErrorMessage name='propiedad' component={ErrorStyled}></ErrorMessage> */}
              <SectionTitle>Dirección</SectionTitle>
              <ContainerInput>
                <label>Provincia:</label>
                <Field name="provincias">
                  {({ field, form }) => (
                    <select
                      {...field}
                      id="provincias"
                      onChange={(e) => {
                        field.onChange(e);
                        handleChangeProvincia(e);
                      }}
                    >
                      <option value="">Seleccioná una provincia</option>;
                      {provincias.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.nombre}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </ContainerInput>
              <ErrorMessage
                name="provincias"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Departamento:</label>
                {loadingDepartamentos ? (
                  <Spinner />
                ) : (
                  <Field name="departamentos">
                    {({ field, form }) => (
                      <select
                        {...field}
                        id="departamentos"
                        onChange={(e) => {
                          field.onChange(e);
                          handleChangeDepartamentos(e);
                        }}
                      >
                        <option value="">Seleccioná un departamento</option>;
                        {departamentos.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                )}
              </ContainerInput>
              <ErrorMessage
                name="departamentos"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Localidad:</label>
                {loadingLocalidades ? (
                  <Spinner />
                ) : (
                  <Field name="localidades">
                    {({ field, form }) => (
                      <select
                        name="localidades"
                        onChange={(e) => {
                          field.onChange(e);
                          handleChangeLocalidades(e);
                        }}
                        value={localSelec}
                      >
                        <option value="">Seleccioná una localidad</option>;
                        {localidades.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                )}
              </ContainerInput>
              <ErrorMessage
                name="localidades"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Calle:</label>
                {/* <InputPredictivo data={calles} /> */}
                <Field name="calle" component={InputPredictivo} data={calles} />
              </ContainerInput>
              <ErrorMessage name="calle" component={ErrorStyled}></ErrorMessage>
              <ContainerInput>
                <label>Altura:</label>
                <Field
                  name="puerta"
                  type="text"
                  id="puerta"
                  as={InputStyled}
                  placeholder="Ej. 1234"
                />
              </ContainerInput>
              <ErrorMessage
                name="puerta"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerInput>
                <label>Piso:</label>
                <input
                  name="piso"
                  type="text"
                  id="piso"
                  as={InputStyled}
                  placeholder="Ej. 1"
                  onChange={(event) => dispatch(setPiso(event.target.value))}
                ></input>
              </ContainerInput>
              <ErrorMessage name="piso" component={ErrorStyled}></ErrorMessage>
              <ContainerInput>
                <label>Dpto:</label>
                <input
                  name="dpto"
                  type="text"
                  id="dpto"
                  as={InputStyled}
                  placeholder="Ej. E"
                  onChange={(event) => dispatch(setDpto(event.target.value))}
                ></input>
              </ContainerInput>
              <ErrorMessage name="dpto" component={ErrorStyled}></ErrorMessage>
              <ContainerInput>
                <label>Ubicación:</label>
                <Field name="ubicacion">
                  {({ field }) => (
                    <div style={{ width: "100%" }}>
                      <textarea
                        {...field}
                        id="ubicacion"
                        placeholder="Ej. Calle Falsa esq. Otra Calle, Barrio, Referencia, etc."
                        maxLength={200}
                        style={{
                          resize: "none",
                          width: "100%",
                          height: "100px",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "1.5px solid #ddd",
                          fontFamily: "'Quicksand', sans-serif",
                          fontSize: "0.88rem",
                          color: "var(--grisOscuro)",
                          outline: "none",
                          boxSizing: "border-box",
                          transition: "border-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "var(--violetaOscuro)";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(76, 4, 140, 0.08)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#ddd";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          textAlign: "right",
                          marginTop: "4px",
                        }}
                      >
                        {field.value?.length || 0}/200
                      </div>
                    </div>
                  )}
                </Field>
              </ContainerInput>
              <ErrorMessage name="ubicacion" component={ErrorStyled} />
              <SectionTitle>Plan comercial</SectionTitle>
              <ContainerInput>
                <label>Planes comerciales:</label>
                <Field name="planComercial">
                  {({ field, form }) => (
                    <select
                      id="planComercial"
                      onChange={(e) => {
                        field.onChange(e);
                        handleChangePlanes(e);
                      }}
                      value={planSel}
                    >
                      <option>Seleccioná un plan comercial</option>;
                      {Array.isArray(planesComerciales) &&
                        planesComerciales.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.nombre}
                          </option>
                        ))}
                    </select>
                  )}
                </Field>
              </ContainerInput>
              <ErrorMessage
                name="planComercial"
                component={ErrorStyled}
              ></ErrorMessage>
              <ContainerCards>
                {/* <Card data={planesComercialesItem} /> */}
                {loadingCard ? (
                  <Spinner />
                ) : (
                  planesComercialesItem
                    .filter((item) => item?.data?.length > 0)
                    .map((item, index) => (
                      <Tarjeta
                        key={index}
                        item={item}
                        index={index}
                        actualizarSubtotal={actualizarSubtotal}
                      />
                    ))
                )}
                <button
                  onClick={() => {
                    registrarEvento("plan.limpiar", "Limpió el plan comercial");
                    resetPlanComercial();
                  }}
                  className="botonReset"
                  type="button"
                  disabled={planesComercialesItem.length === 0}
                >
                  Limpiar Plan Comercial
                </button>
                <ContainerPrice>
                  {/* <ContainerSubtotal>
                  <label>Subtotal: </label>
                  <label>{formatoPrecio(subtotal)}</label>
                </ContainerSubtotal> */}
                  <ContainerTotal>
                    <label>Total primer factura(*): </label>
                    <label>{formatoPrecio(total)}</label>
                  </ContainerTotal>
                  <label>
                    * Más el importe proporcional correspondiente a los días
                    previos al comienzo del mes de abono.
                  </label>
                </ContainerPrice>
              </ContainerCards>
              <ContainerButton>
                <ButtonForm
                  type="submit"
                  disabled={
                    confirmados.length === 0 ||
                    !confirmados.every(Boolean) ||
                    submitted ||
                    enviando
                  }
                  progreso={envioOk ? "ok" : enviando ? "enviando" : undefined}
                >
                  {envioOk ? "✓ Guardado" : enviando ? "Enviando..." : "Guardar"}
                </ButtonForm>
              </ContainerButton>
              <button
                onClick={() => {
                  registrarEvento("formulario.limpiar", "Limpió el formulario");
                  // Forzar vacío: si vino precargado desde una solicitud, los
                  // initialValues son los datos de esa solicitud, así que un
                  // resetForm() sin argumentos los volvería a poner.
                  resetForm({ values: initialValuesFormulario });
                  resetVenta();
                  setPrefillVisible(false);
                }}
                className="botonReset"
                type="button"
              >
                Limpiar Formulario
              </button>
            </ContainerForm>
          )}
        </Formik>
      )}
      {modalOpen && (
        <Modal isOpen={true} onClose={() => setModalOpen(false)}>
          <h2>{modalMessage}</h2>
        </Modal>
      )}
      {confirmAlta && (
        <Modal
          isOpen={true}
          onConfirm={confirmarAlta}
          onClose={() => setConfirmAlta(null)}
          confirmText="Sí, dar el alta"
          cancelText="No, modificar"
        >
          <h2>
            ¿Seguro que querés dar el alta
            {cuotasDeDetalle(confirmAlta.activacion ? [confirmAlta.activacion] : [])
              ? ` con el costo de activación en ${cuotasDeDetalle([confirmAlta.activacion])}`
              : ""}
            ?
          </h2>
          {confirmAlta.activacion ? (
            <p>
              Costo de activación: <strong>{confirmAlta.activacion.politica}</strong>
              {confirmAlta.activacion.precio != null
                ? ` — ${formatoPrecio(confirmAlta.activacion.precio)}`
                : ""}
            </p>
          ) : (
            <p>Revisá el costo de activación antes de confirmar.</p>
          )}
          <p style={{ color: "var(--grisMedio)", fontSize: "0.9rem" }}>
            {confirmAlta.planNombre ? `Plan: ${confirmAlta.planNombre} · ` : ""}
            Total 1ª factura: {formatoPrecio(confirmAlta.total || 0)}
          </p>
        </Modal>
      )}
    </>
  );
};

export default Formulario;
